import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft, RefreshCw, ShieldAlert, Shield, Users, DollarSign,
  CheckCircle, Clock, AlertCircle, Search, Crown, Download
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (val) => {
  if (!val) return "—";
  try {
    return new Date(val).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric"
    });
  } catch {
    return "—";
  }
};

const statusStyle = (status) => {
  switch (status) {
    case "admin":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "active":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "trial":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "expired":
    default:
      return "bg-slate-200 text-slate-700 border-slate-300";
  }
};

const planLabel = (plan) => {
  if (plan === "yearly") return "Yearly ($49.90)";
  if (plan === "monthly") return "Monthly ($4.99)";
  return "—";
};

const AdminSubscribersPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/subscribers`, { withCredentials: true });
      setData(response.data);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
      if (err.response?.status === 403) {
        setError("access_denied");
      } else {
        toast.error("Failed to load subscribers");
        setError("fetch_error");
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data?.subscribers) return [];
    return data.subscribers.filter((s) => {
      const matchesSearch =
        !search ||
        s.email?.toLowerCase().includes(search.toLowerCase()) ||
        s.name?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || s.subscription_status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter]);

  const downloadCSV = () => {
    if (!data?.subscribers?.length) return;
    const headers = [
      "Email", "Name", "Status", "Plan", "Total Paid (USD)", "Payments",
      "Subscription End", "Trial End", "Last Payment", "Signed Up", "Auth"
    ];
    const rows = data.subscribers.map((s) => [
      s.email, s.name || "",
      s.subscription_status,
      s.subscription_plan || "",
      (s.total_paid || 0).toFixed(2),
      s.payments_count || 0,
      s.subscription_end || "",
      s.trial_end || "",
      s.last_payment_at || "",
      s.created_at || "",
      s.auth_method || ""
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `owl-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error === "access_denied") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full shadow-lg border-red-200" data-testid="admin-access-denied">
          <CardContent className="text-center py-12">
            <ShieldAlert className="h-16 w-16 mx-auto mb-4 text-red-400" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-6">
              This page is restricted to administrators only.
            </p>
            <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = [
    {
      label: "Total Users",
      value: data?.total_users ?? 0,
      icon: Users,
      color: "text-slate-700",
      bg: "bg-slate-100"
    },
    {
      label: "Active Subscribers",
      value: data?.active_subscribers ?? 0,
      icon: CheckCircle,
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    },
    {
      label: "Trialing",
      value: data?.trialing ?? 0,
      icon: Clock,
      color: "text-blue-600",
      bg: "bg-blue-50"
    },
    {
      label: "Total Revenue",
      value: `$${(data?.total_revenue ?? 0).toFixed(2)}`,
      icon: DollarSign,
      color: "text-amber-600",
      bg: "bg-amber-50"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-subscribers-page">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif"
                alt="Owl Insights Logo"
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">Owl Insights</h1>
                <p className="text-xs text-slate-500">Admin · Subscribers</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="export-csv-btn"
                onClick={downloadCSV}
                variant="ghost"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium"
                disabled={!data?.subscribers?.length}
              >
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
              <Button
                data-testid="refresh-subscribers-btn"
                onClick={fetchSubscribers}
                variant="ghost"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button
                data-testid="back-account-btn"
                onClick={() => navigate("/account")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Account
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert className="bg-purple-50 border-purple-200 mb-6">
          <Shield className="h-5 w-5 text-purple-600" />
          <AlertDescription className="text-purple-900">
            <span className="font-semibold">Admin Panel</span> — Subscriber & Revenue Overview
          </AlertDescription>
        </Alert>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <Card key={s.label} className="shadow-sm border border-slate-200" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center ${s.bg}`}>
                    <s.icon className={`h-6 w-6 ${s.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="shadow-sm border border-slate-200 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="subscribers-search-input"
                  placeholder="Search by email or name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { key: "all", label: "All" },
                  { key: "active", label: "Active" },
                  { key: "trial", label: "Trial" },
                  { key: "expired", label: "Expired" },
                  { key: "admin", label: "Admin" }
                ].map((f) => (
                  <Button
                    key={f.key}
                    data-testid={`filter-${f.key}-btn`}
                    variant={statusFilter === f.key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter(f.key)}
                    className={statusFilter === f.key ? "bg-blue-600 hover:bg-blue-700" : ""}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="shadow-sm border border-slate-200">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50/50">
            <CardTitle className="text-lg">
              Subscribers ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-20">
                <RefreshCw className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-slate-500">Loading subscribers…</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <AlertCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No subscribers match your filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3 font-semibold">User</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Plan</th>
                      <th className="px-4 py-3 font-semibold text-right">Total Paid</th>
                      <th className="px-4 py-3 font-semibold">Next Renewal</th>
                      <th className="px-4 py-3 font-semibold">Trial End</th>
                      <th className="px-4 py-3 font-semibold">Signed Up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => (
                      <tr
                        key={s.id}
                        className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
                        data-testid={`subscriber-row-${s.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {s.picture ? (
                              <img src={s.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-semibold">
                                {(s.name || s.email || "?").charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-1 font-medium text-slate-900">
                                <span className="truncate max-w-[200px]">{s.email}</span>
                                {s.is_admin && (
                                  <Crown className="h-3.5 w-3.5 text-purple-600" title="Admin" />
                                )}
                              </div>
                              <p className="text-xs text-slate-500 truncate max-w-[220px]">
                                {s.name || "—"} · {s.auth_method}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${statusStyle(s.subscription_status)} border capitalize`}>
                            {s.subscription_status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{planLabel(s.subscription_plan)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          ${(s.total_paid || 0).toFixed(2)}
                          {s.payments_count > 0 && (
                            <span className="text-xs text-slate-400 ml-1">({s.payments_count})</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(s.subscription_end)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(s.trial_end)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(s.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSubscribersPage;
